import React from 'react';
import { 
  FaOilCan, FaCarBattery, FaCog, FaWrench, FaTruck, FaMotorcycle, FaBolt, 
  FaGasPump, FaTractor, FaShieldAlt, FaRoad, FaThermometerHalf, FaTint, 
  FaWind, FaGlobe, FaChargingStation, FaShieldVirus, FaTools, FaRecycle, 
  FaFire, FaUserCog 
} from 'react-icons/fa';
import '../styles/CityEnterprises.css';

// Import images for lubricant issues
import OilLeaks from '../images/oilleaks.webp';
import Overheating from '../images/overheating.webp';
import HardGear from '../images/hardgear.webp';
import DirtyOil from '../images/dirtyoil.webp';

const lubricantsData = [
  { icon: <FaOilCan />, name: "Engine Oil", use: "Reduces friction and wear in the engine." },
  { icon: <FaCarBattery />, name: "Transmission Fluid", use: "Ensures smooth gear shifting." },
  { icon: <FaCog />, name: "Gear Oil", use: "Lubricates gears for efficient performance." },
  { icon: <FaWrench />, name: "Brake Fluid", use: "Enhances braking response and safety." },
  { icon: <FaTruck />, name: "Grease", use: "Prevents metal-to-metal contact in joints." },
  { icon: <FaMotorcycle />, name: "2-Wheeler Oil", use: "Optimized for bikes and scooters." },
  { icon: <FaBolt />, name: "Coolant", use: "Maintains optimal engine temperature." },
  { icon: <FaGasPump />, name: "Diesel Engine Oil", use: "Improves fuel efficiency and engine life." },
  { icon: <FaTractor />, name: "Hydraulic Oil", use: "Used in heavy machinery and tractors." },
  { icon: <FaShieldAlt />, name: "Synthetic Oil", use: "Long-lasting protection for modern engines." },
  { icon: <FaRoad />, name: "Differential Oil", use: "Enhances drivetrain efficiency." },
  { icon: <FaThermometerHalf />, name: "High-Temp Grease", use: "Works under extreme heat conditions." },
  { icon: <FaTint />, name: "Power Steering Fluid", use: "Ensures smooth steering movement." },
  { icon: <FaWind />, name: "Compressor Oil", use: "Lubricates air compressors for efficiency." },
  { icon: <FaGlobe />, name: "Marine Lubricant", use: "Protects engines in marine environments." },
  { icon: <FaChargingStation />, name: "EV Lubricant", use: "Optimized for electric vehicles." },
  { icon: <FaShieldVirus />, name: "Antifreeze", use: "Prevents coolant from freezing in cold climates." },
  { icon: <FaTools />, name: "Assembly Lube", use: "Used for component pre-lubrication." },
  { icon: <FaRecycle />, name: "Eco-Friendly Oil", use: "Biodegradable and environmentally safe." },
  { icon: <FaFire />, name: "Fire-Resistant Fluid", use: "Protects equipment in high-temperature conditions." },
];

const lubricantIssues = [
  { image: OilLeaks, title: "🛢️ Oil Leaks", description: "Check for loose drain plugs or worn-out seals." },
  { image: DirtyOil, title: "🚗 Dirty Oil", description: "Change oil every 5,000-10,000 km for optimal performance." },
  { image: Overheating, title: "🔥 Overheating", description: "Ensure coolant levels are maintained." },
  { image: HardGear, title: "🔧 Hard Gear Shifting", description: "Use the correct transmission fluid recommended by the manufacturer." },
];

const LubricantSlide = ({ lubricants, text }) => (
  <div className="lubricant-slide">
    <div className="lubricant-cards">
      {lubricants.map((lubricant, index) => (
        <div key={index} className="lubricant-card">
          <div className="lubricant-icon">{lubricant.icon}</div>
          <h3>{lubricant.name}</h3>
          <p>{lubricant.use}</p>
        </div>
      ))}
    </div>
    <p className="lubricant-info-text">{text}</p>
  </div>
);

const LubricantIssues = () => (
  <div className="lubricant-issues">
    <h2>Common Lubricant Issues & Fixes</h2>
    <div className="issue-container">
      {lubricantIssues.map((issue, index) => (
        <div key={index} className="issue-card">
          <img src={issue.image} alt={issue.title} />
          <h3>{issue.title}</h3>
          <p>{issue.description}</p>
        </div>
      ))}
    </div>
  </div>
);

const CityEnterprises = () => {
  return (
    <div className="city-enterprises-container">
      <h2>How Lubricants Help Vehicles</h2>

      <LubricantSlide lubricants={lubricantsData.slice(0, 5)} text="Essential lubricants like Engine Oil, Transmission Fluid, and Brake Fluid help in reducing wear, improving efficiency, and ensuring smooth operation." />
      <LubricantSlide lubricants={lubricantsData.slice(5, 10)} text="Specialized lubricants such as Coolant, Hydraulic Oil, and Synthetic Oil improve performance under extreme conditions." />
      <LubricantSlide lubricants={lubricantsData.slice(10, 15)} text="High-performance lubricants like Compressor Oil, Differential Oil, and Power Steering Fluid enhance durability and safety." />
      <LubricantSlide lubricants={lubricantsData.slice(15, 20)} text="Innovative solutions such as EV Lubricants, Eco-Friendly Oils, and Fire-Resistant Fluids ensure sustainability and protection." />

      {/* Common Lubricant Issues Section */}
      <LubricantIssues />

      {/* --- Lubricant Tips for Maximum Performance --- */}
      <div className="lubricant-tips">
        <h2>Lubricant Tips for Maximum Performance</h2>
        <ul className="white-text">
          <li> Always use the  right grade of lubricant for your engine.</li>
          <li> Change oil filters every two oil changes to keep it clean.</li>
          <li> Don’t mix different types of lubricants.</li>
        </ul>
      </div>

      {/* --- Mechanic Testimonials --- */}
      <div className="mechanic-testimonials">
        <h2>What Mechanics Say</h2>
        <p><FaUserCog /> "Using high-quality synthetic oil has increased my customers' engine life by 30%." <strong>Balmerol User</strong></p>
        <p><FaUserCog /> "Transmission issues are 90% of the time due to bad fluid. Keep it clean!" <strong>Chemoleum User</strong></p>
      </div>

      {/* --- Why Choose Our Lubricants --- */}
      <div className="why-choose-lubricants">
        <h2>Why Choose Our Lubricants?</h2>
        <div className="why-choose-container">
          <div className="why-choose-item">
            <FaShieldAlt className="why-choose-icon" />
            <h3>Superior Protection</h3>
            <p>Our lubricants are engineered to shield your engine from wear and tear, extending its lifespan.</p>
          </div>
          <div className="why-choose-item">
            <FaRecycle className="why-choose-icon" />
            <h3>Eco-Conscious</h3>
            <p>We offer biodegradable options that reduce environmental impact without compromising performance.</p>
          </div>
          <div className="why-choose-item">
            <FaBolt className="why-choose-icon" />
            <h3>Enhanced Efficiency</h3>
            <p>Optimized formulas improve fuel economy and ensure smoother vehicle operation.</p>
          </div>
        </div>
      </div>

      {/* --- Lubricant Maintenance Guide --- */}
      <div className="maintenance-guide">
        <h2>Lubricant Maintenance Guide</h2>
        <div className="guide-container">
          <div className="guide-item">
            <h3>Check Levels Monthly</h3>
            <p>Monitor oil and coolant levels to prevent unexpected breakdowns.</p>
          </div>
          <div className="guide-item">
            <h3>Follow Manufacturer Specs</h3>
            <p>Use the recommended lubricant type and grade for your vehicle.</p>
          </div>
          <div className="guide-item">
            <h3>Schedule Regular Changes</h3>
            <p>Replace lubricants every 6-12 months or as per mileage guidelines.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityEnterprises;